import { useForm } from '@inertiajs/react'
import { useState } from 'react'
import { Download, Plus } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { RedirectRuleForm } from '@/components/help_center/RedirectRuleForm'
import { RedirectRuleList } from '@/components/help_center/RedirectRuleList'
import { RedirectImport } from '@/components/help_center/RedirectImport'
import type { RedirectRule, RedirectPreview } from '@/components/help_center/redirectTypes'
import { useRouteParams } from '@/hooks/useRoutes'
import routes from '@/routeHelpers'

export interface HelpRedirectsData {
  rules: RedirectRule[]
  digest: string
  brand: string
  warnings?: { source: string; message: string }[]
  preview: RedirectPreview | null
}
const textMap = (rules: RedirectRule[]) => rules.map(r => `${r.source} ${r.destination} ${r.status}`).join('\n')

export function HelpCenterRedirects({ rules, digest, brand, preview, warnings = [] }: HelpRedirectsData) {
  const { withAccount } = useRouteParams()
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<RedirectRule | null>(null)
  const [previewedInput, setPreviewedInput] = useState('')
  const form = useForm({ content: '', import_format: 'text', mode: 'merge', brand, expected_digest: digest })
  const inputSignature = JSON.stringify([form.data.content, form.data.import_format, form.data.mode])

  const previewMap = (content = form.data.content, mode = form.data.mode, format = form.data.import_format) => {
    form.setData({ ...form.data, content, mode, import_format: format })
    setPreviewedInput(JSON.stringify([content, format, mode]))
    form.transform(data => ({ ...data, content, mode, import_format: format }))
    form.post(routes.settings.previewHelpRedirects.path({ ...withAccount(), query: { tab: 'help-center', help_tab: 'redirects', brand } }), { preserveScroll: true })
  }
  const stageRule = (rule: RedirectRule) => {
    const updated = rules.filter(r => r.source !== editing?.source)
    updated.push(rule)
    previewMap(textMap(updated), 'replace', 'text')
  }
  const apply = () => {
    if (form.processing || !preview || previewedInput !== inputSignature) return
    form.transform(data => ({ ...data, expected_digest: preview.digest }))
    form.post(routes.helpRedirects.import.path(withAccount()), {
      preserveScroll: true, onSuccess: () => { form.setData('content', ''); setPreviewedInput('') },
    })
  }

  return (
    <>
      <div className="space-y-6">
        <div><h3 className="text-lg font-semibold">Redirects</h3>
          <p className="text-sm text-muted-foreground">Keep old help center links working by sending them to their current pages.</p></div>
        <div className="flex flex-wrap gap-3">
          <Button onClick={() => { setEditing(null); setOpen(true) }}><Plus />Add redirect</Button>
          <Button variant="outline" asChild>
            <a href={routes.helpRedirects.export.path({ ...withAccount(), query: { brand, format: 'text' } })} download><Download />Export text</a>
          </Button>
          <Button variant="outline" asChild>
            <a href={routes.helpRedirects.export.path({ ...withAccount(), query: { brand, format: 'csv' } })} download><Download />Export CSV</a>
          </Button>
        </div>
        <RedirectRuleList rules={rules} warnings={warnings} onEdit={rule => { setEditing(rule); setOpen(true) }}
          onDelete={rule => previewMap(textMap(rules.filter(r => r.source !== rule.source)), 'replace', 'text')} />
        <RedirectImport content={form.data.content} format={form.data.import_format} mode={form.data.mode} busy={form.processing} error={form.errors.content}
          preview={preview} previewCurrent={previewedInput === inputSignature}
          onContent={value => form.setData('content', value)} onFormat={value => form.setData('import_format', value)} onMode={value => form.setData('mode', value)}
          onPreview={() => previewMap()} onApply={apply} />
      </div>
      <RedirectRuleForm key={`${editing?.source ?? 'new'}-${open}`} rule={editing} open={open} onOpenChange={setOpen} onPreview={stageRule} />
    </>
  )
}
